/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback, useState, useEffect } from 'react';
import fetch from '@api/fetchers';
import { HOST_TYPE } from '@utils/api-helper/host';
import getPaginationResults from '@utils/swrHelper';
import useSWR from 'swr';
import getQueryString from '@utils/getQueryString';
import { processCourse } from '@api/processors/course';

const defaultSwrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  dedupingInterval: 15000
};

const useInfiniteFetchNew = (
  key: string,
  fetcherOptions,
  swrOptions = null
) => {
  const [size, setSize] = useState(0);

  const {
    useAuthToken = true,
    useSearchToken = false,
    isFormData = false,
    hostType = HOST_TYPE.DEFAULT,
    hasAppClient = false,
    params = {},
    pathVars = {},
    useCamelCase = true,
    applyOffsetsAutomatically = true,
    hasQueryParams = false
  } = fetcherOptions || {};

  const options = useMemo(() => {
    return {
      useAuthToken,
      useSearchToken,
      isFormData,
      hostType,
      hasAppClient,
      params,
      pathVars,
      useCamelCase,
      applyOffsetsAutomatically,
      hasQueryParams
    };
  }, [
    useAuthToken,
    useSearchToken,
    isFormData,
    useCamelCase,
    hostType,
    hasAppClient,
    params,
    pathVars,
    applyOffsetsAutomatically,
    hasQueryParams,
    size
  ]);

  const fetcher = useCallback(
    (...args: any[]) => {
      return fetch(...args, {
        ...options,
        params: undefined
      });
    },
    [key, options]
  );

  const formattedKey = useMemo(() => {
    return `${key}${getQueryString({
      ...(options.params || {}),
      offset: size * 12
    })}`;
  }, [key, options, size]);

  const { data, error, ...rest } = useSWR(formattedKey, fetcher, {
    ...defaultSwrOptions,
    ...(swrOptions || {})
  });

  const [cumulativeData, setCumulativeData] = useState<any[]>(() => [
    {
      ...data,
      results: data.results?.map((item) =>
        processCourse({ ...item, ...(item.programme || {}) })
      )
    }
  ]);

  useEffect(() => {
    if (data?.results?.length) {
      setCumulativeData((prev) => {
        const processedData = {
          ...data,
          results: data.results?.map((item) =>
            processCourse({ ...item, ...(item.programme || {}) })
          )
        };
        if (size > 0) {
          return [...prev, processedData];
        }
        return [processedData];
      });
    }
  }, [data, size]);

  return {
    key,
    rawData: cumulativeData,
    data: cumulativeData
      ? getPaginationResults(cumulativeData)
      : { count: 0, results: [] },
    size,
    setSize,
    isLoading: !error && !data,
    isError: error,
    ...rest // rest -> isValidating, mutate
  };
};

export default useInfiniteFetchNew;
